import React, { useEffect, useRef, useState } from 'react';
import {
    getNetologyCourse,
    getNetologyCourses,
    defaultCalendarIds,
    bulkEvents,
    getTokenFromLocalStorage,
    getCalendarIdsLocalStorage,
    setCalendarIdsLocalStorage,
    getNetologyCoursesLocalStorage,
    setNetologyCoursesLocalStorage,
    getModeusPersonIdFromLocalStorage,
    getLMSTokenFromLocalStorage,
    getLMSIdFromLocalStorage,
    getIcsSubscriptionUrlLocalStorage,
    getMtsLinks
} from '../services/api';
import CourseSelectorModal from "../components/Calendar/CourseSelectorModal";
import SubscriptionModal from "../components/Calendar/SubscriptionModal";
import GradesModal from "../components/Calendar/GradesModal";
import { toast } from 'react-toastify';
import Loader from "../elements/Loader";
import '../style/header.scss';
import '../style/calendar.scss';
import DatePicker from "../components/Calendar/DataPicker";
import SimpleDatePicker from "../components/Calendar/SimpleDatePicker";
import ExitBtn from "../components/Calendar/ExitBtn";
import LogoutModal from "../components/Calendar/LogoutModal";
import { exitApp, logoutUser } from "../utils/auth";
import CalendarExportMenu from "../components/Calendar/CalendarExportMenu";
import SettingsMenu from "../components/Calendar/SettingsMenu";
import GradesBtn from "../components/Calendar/GradesBtn";
import CacheUpdateBtn from "../components/Calendar/CacheUpdateBtn";
import ServiceStatusBanner from "../components/Calendar/ServiceStatusBanner";
import ServiceRestartNotice from "../components/Calendar/ServiceRestartNotice";
import { isServiceRestarting, RESTART_RETRY_DELAYS } from '../utils/serviceRestart';
import ServiceHealthIndicator from "../components/Calendar/ServiceHealthIndicator";
import { getCurrentWeekDates } from "../utils/dateUtils";
import EventsDetail from "../components/Calendar/EventsDetail";
import EventModal from "../components/Calendar/EventModal";
import MobileCalendarView from "../components/Calendar/MobileCalendarView";
import DeadLine from "../components/Calendar/DeadLine";
import DaysNumber from "../components/Calendar/DaysNumber";
import LessonTimes from "../components/Calendar/LessonTimes";
import GitHubStarModal from "../components/GitHubStarModal";
import FeaturesModal from "../components/FeaturesModal";
import { debug } from "../utils/debug";
import { useNavigate, useSearchParams } from "react-router-dom";
import { handleApiError } from '../utils/errorHandler';
import { prefetchModeusGrades } from '../services/modeusGrades';
import ErrorBoundary from '../elements/ErrorBoundary';

const CalendarPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const cacheOnly = searchParams.get('cache_only') === 'true';
    const [cacheMessage, setCacheMessage] = useState('');
    const [date, setDate] = useState(() => getCurrentWeekDates());
    const [events, setEvents] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showGithubModal, setShowGithubModal] = useState(false);
    const [showFeaturesModal, setShowFeaturesModal] = useState(false);
    const [mtsUrls, setMtsUrls] = useState({});
    const [calendarIds, setCalendarIds] = useState(() => getCalendarIdsLocalStorage());
    const [netologyCourses, setNetologyCourses] = useState(() => getNetologyCoursesLocalStorage());
    const [showCourseModal, setShowCourseModal] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [showGradesModal, setShowGradesModal] = useState(false);

    const navigate = useNavigate();
    const lastFetchedDate = useRef(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    // Сброс кэша живет в CacheUpdateBtn; баннер о недоступных сервисах
    // дергает его через ref, чтобы не дублировать логику обновления.
    const refreshRef = useRef(null);
    const [retryingServices, setRetryingServices] = useState(false);
    // Бэкенд перезапускается (деплой): ждем и пробуем снова сами. Таймер — в ref;
    // после ухода со страницы цепочка повторов не должна жить дальше.
    const [restarting, setRestarting] = useState(false);
    const restartTimerRef = useRef(null);
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
        clearTimeout(restartTimerRef.current);
    }, []);
    const handleRetryServices = async () => {
        if (!refreshRef.current || retryingServices) return;
        setRetryingServices(true);
        try {
            await refreshRef.current();
        } finally {
            setRetryingServices(false);
        }
    };

    // Осознанный выход: если есть подписка — спрашиваем, что с ней делать
    const handleLogoutRequest = () => {
        if (getIcsSubscriptionUrlLocalStorage()) {
            setShowLogoutModal(true);
        } else {
            logoutUser(navigate);
        }
    };

    // Оценки за пары показываются в карточках — тянем их фоном, один раз,
    // когда календарь уже загрузился (только с «Запомнить меня»).
    const gradesPrefetched = useRef(false);
    useEffect(() => {
        if (!cacheOnly && events && !gradesPrefetched.current) {
            gradesPrefetched.current = true;
            prefetchModeusGrades().catch((e) => debug.log('Оценки фоном не загрузились:', e));
        }
    }, [events, cacheOnly]);

    // Проверяем наличие всех необходимых токенов при загрузке страницы
    useEffect(() => {
        if (cacheOnly) return;
        // calendarIds здесь сознательно не проверяем: у старых пользователей
        // их может не быть, fetchData сам подгрузит список курсов по токену.
        const requiredTokens = {
            'lms-id': getLMSIdFromLocalStorage(),
            'lms-token': getLMSTokenFromLocalStorage(),
            'modeus_person_id': getModeusPersonIdFromLocalStorage(),
            'token': getTokenFromLocalStorage()
        };

        const missingTokens = [];
        for (const [key, value] of Object.entries(requiredTokens)) {
            if (!value) {
                missingTokens.push(key);
            }
        }

        if (missingTokens.length > 0) {
            if (getModeusPersonIdFromLocalStorage() && getCalendarIdsLocalStorage().length > 0) {
                setSearchParams({cache_only: 'true'}, {replace: true});
                return;
            }
            debug.error('Missing required tokens:', missingTokens);
            toast.error("Отсутствуют данные авторизации. Необходимо войти заново.");
            exitApp(navigate);
            return;
        }
    }, [navigate, cacheOnly, setSearchParams]);

    // Проверяем, нужно ли показать модальное окно GitHub Star
    useEffect(() => {
        const hasSeenGithubModal = localStorage.getItem('githubStarModalShown');
        const remindDateStr = localStorage.getItem('githubStarRemindDate');
        const firstVisitStr = localStorage.getItem('calendarFirstVisit');
        
        // Сохраняем дату первого визита, если её нет
        if (!firstVisitStr) {
            const firstVisitDate = new Date();
            localStorage.setItem('calendarFirstVisit', firstVisitDate.toISOString());
            return; // Не показываем модальное окно в первый визит
        }
        
        let shouldShowModal = false;
        const firstVisitDate = new Date(firstVisitStr);
        const currentDate = new Date();
        const daysSinceFirstVisit = (currentDate - firstVisitDate) / (1000 * 60 * 60 * 24);
        
        if (!hasSeenGithubModal && !remindDateStr) {
            // Показываем модальное окно только если прошла неделя с первого визита
            if (daysSinceFirstVisit >= 7) {
                shouldShowModal = true;
            }
        } else if (remindDateStr && !hasSeenGithubModal) {
            // Проверяем, прошла ли неделя с момента отложенного напоминания
            const remindDate = new Date(remindDateStr);
            
            if (currentDate >= remindDate) {
                // Время напоминания наступило
                shouldShowModal = true;
                // Удаляем дату напоминания, чтобы не показывать повторно
                localStorage.removeItem('githubStarRemindDate');
            }
        }
        
        if (shouldShowModal) {
            // Показываем модальное окно с небольшой задержкой для лучшего UX
            const timer = setTimeout(() => {
                setShowGithubModal(true);
            }, 2000); // 2 секунды после загрузки страницы
            
            return () => clearTimeout(timer);
        }
    }, []);

    useEffect(() => {
        const dateKey = `${date.start}_${date.end}_${[...calendarIds].sort().join(',')}_${cacheOnly}`;
        if (lastFetchedDate.current === dateKey) return;
        lastFetchedDate.current = dateKey;

        // Очищаем выбранное событие при смене недели
        setSelectedEvent(null);
        setCacheMessage('');
        setEvents(null);
        
        // Запускаем анимацию перехода
        setIsTransitioning(true);

        // Ответ недели, которую уже пролистали, не должен лечь поверх текущей:
        // при быстром листании запросы идут параллельно и приходят вразнобой.
        const isCurrent = () => lastFetchedDate.current === dateKey;

        // attempt — номер повтора, пока бэкенд перезапускается (см. catch ниже).
        const fetchData = async (attempt = 0) => {
            setLoading(true);

            try {
                let ids = calendarIds;

                // Если выбранных курсов нет — подгружаем список и берем все по умолчанию
                if (!cacheOnly && (!ids || ids.length === 0)) {
                    const coursesData = await getNetologyCourses(getTokenFromLocalStorage());
                    const programs = coursesData?.programs || [];
                    if (programs.length > 0) {
                        setNetologyCoursesLocalStorage(programs);
                        setNetologyCourses(programs);
                        ids = defaultCalendarIds(programs);
                    } else {
                        // Fallback на старое поведение (один курс)
                        const courseData = await getNetologyCourse(getTokenFromLocalStorage());
                        ids = courseData?.id ? [courseData.id] : [];
                    }
                    setCalendarIdsLocalStorage(ids);
                    setCalendarIds(ids);
                }

                if (!ids || ids.length === 0) {
                    debug.error('Ошибка при получении calendar ids:', ids);
                    toast.error("Не удалось загрузить календарь. Попробуйте снова.");
                    return;
                }

                const eventsResponse = await bulkEvents({
                    calendarIds: ids,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    timeMin: date.start,
                    timeMax: date.end,
                    sessionToken: getTokenFromLocalStorage(),
                    modeusPersonId: getModeusPersonIdFromLocalStorage(),
                    lxpToken: getLMSTokenFromLocalStorage(),
                    lxpId: getLMSIdFromLocalStorage(),
                    cacheOnly
                });

                if (!isCurrent()) return;

                if (eventsResponse?.data) {
                    setEvents(eventsResponse.data);
                    setRestarting(false);

                    // Извлекаем timestamp кэша для передачи в CacheUpdateBtn
                    debug.log('📅 Cache timestamp from API:', eventsResponse.data.cached_at);
                    
                    // Получаем все Modeus события и загружаем их MTS ссылки
                    const modeusEvents = eventsResponse.data?.utmn?.modeus_events || [];
                    const lessonIds = modeusEvents.map(event => event.id).filter(Boolean);
                    
                    if (!cacheOnly && lessonIds.length > 0) {
                        try {
                            const mtsResponse = await getMtsLinks(lessonIds);
                            if (isCurrent() && mtsResponse?.status === 200 && mtsResponse.data?.links) {
                                setMtsUrls(mtsResponse.data.links);
                            }
                        } catch (error) {
                            debug.error('Error loading MTS URLs:', error);
                            // Не показываем пользователю ошибку, так как это не критично
                        }
                    }
                } else {
                    toast.error("Не удалось загрузить события. Повторите попытку.");
                    debug.error("Пустой ответ от bulkEvents:", eventsResponse);
                }

            } catch (error) {
                if (!isCurrent()) return;
                debug.error('Ошибка при получении данных с сервера:', error);
                if (cacheOnly) {
                    setEvents(null);
                    const status = error?.response?.status;
                    setCacheMessage(status === 404
                        ? 'Эта неделя не сохранена. Выберите ранее загруженную неделю или войдите, чтобы загрузить новые данные.'
                        : status === 401 || status === 403
                            ? 'Доступ к сохраненному расписанию истек. Войдите заново.'
                            : 'Не удалось прочитать сохраненное расписание. Попробуйте позже.');
                    return;
                }
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    setSearchParams({cache_only: 'true'}, {replace: true});
                    return;
                }

                // Деплой: прокси отвечает HTML-страницей 502/503/504 или не отвечает вовсе.
                // Не пугаем ошибкой — спокойное уведомление и повтор сами, все реже.
                const retryDelay = RESTART_RETRY_DELAYS[attempt];
                if (isServiceRestarting(error) && retryDelay !== undefined) {
                    if (!mountedRef.current) return;
                    setEvents(null);
                    setRestarting(true);
                    clearTimeout(restartTimerRef.current);
                    restartTimerRef.current = setTimeout(() => {
                        if (mountedRef.current && isCurrent()) fetchData(attempt + 1);
                    }, retryDelay);
                    return;
                }
                setRestarting(false);

                // Настоящая ошибка (или повторы кончились) — обычный тост
                handleApiError(error, "Ошибка при загрузке расписания.", navigate, { restartAware: false });

            } finally {
                // Загрузку завершает только запрос текущей недели.
                if (isCurrent()) {
                    setLoading(false);
                    // Небольшая задержка для завершения анимации
                    setTimeout(() => setIsTransitioning(false), 100);
                }
            }
        };

        fetchData();
    }, [date, navigate, calendarIds, cacheOnly, setSearchParams]);

    // Открытие модалки выбора курсов (при необходимости подгружаем их список)
    const handleOpenCourseModal = async () => {
        if (netologyCourses.length === 0) {
            const coursesData = await getNetologyCourses(getTokenFromLocalStorage());
            const programs = coursesData?.programs || [];
            if (programs.length > 0) {
                setNetologyCoursesLocalStorage(programs);
                setNetologyCourses(programs);
            }
        }
        setShowCourseModal(true);
    };

    // Сохранение нового набора курсов: обновляем localStorage и перезапрашиваем события
    const handleSaveCourses = (ids) => {
        setCalendarIdsLocalStorage(ids);
        setCalendarIds(ids);
    };

    const handleDataUpdate = (updatedEvents) => {
        setEvents(updatedEvents);
    };

    const handleCloseGithubModal = () => {
        setShowGithubModal(false);
    };

    // «Мои оценки» из карточки пары: карточка закрывается, окно оценок — поверх календаря.
    const handleOpenGradesFromCard = () => {
        setSelectedEvent(null);
        setShowGradesModal(true);
    };

    const handleCloseEventModal = () => {
        setSelectedEvent(null);
    };

    return (
        <div className="calendar-page">
            <GitHubStarModal 
                isOpen={showGithubModal}
                onClose={handleCloseGithubModal}
            />
            <FeaturesModal 
                isOpen={showFeaturesModal}
                onClose={() => setShowFeaturesModal(false)}
                onOpenGithubModal={() => setShowGithubModal(true)}
            />
            {/* Сломанная карточка пары не должна снимать весь календарь */}
            <ErrorBoundary resetKey={selectedEvent}>
                <EventModal
                    event={selectedEvent}
                    isOpen={!!selectedEvent}
                    onClose={handleCloseEventModal}
                    mtsUrls={mtsUrls}
                    onOpenGrades={handleOpenGradesFromCard}
                />
            </ErrorBoundary>
            <CourseSelectorModal
                isOpen={showCourseModal}
                onClose={() => setShowCourseModal(false)}
                courses={netologyCourses}
                selectedIds={calendarIds}
                onSave={handleSaveCourses}
            />
            <SubscriptionModal
                isOpen={showSubscriptionModal}
                onClose={() => setShowSubscriptionModal(false)}
                courses={netologyCourses}
                selectedIds={calendarIds}
            />
            <GradesModal
                isOpen={showGradesModal}
                onClose={() => setShowGradesModal(false)}
            />
            <LogoutModal
                isOpen={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onLogout={(options) => {
                    setShowLogoutModal(false);
                    logoutUser(navigate, options);
                }}
            />
            <div className="wrapper">
                <header className="header">
                    <div className="header-line">
                        <div className="shedule-export">
                            <span className="shedule">Мое расписание <span className="beta-badge">BETA</span></span>
                            <CalendarExportMenu
                                disabled={cacheOnly}
                                date={date}
                                onOpenSubscription={() => setShowSubscriptionModal(true)}
                            />
                            <CacheUpdateBtn 
                                disabled={cacheOnly}
                                date={date} 
                                onDataUpdate={handleDataUpdate}
                                cachedAt={events?.cached_at}
                                failures={events?.failures}
                                refreshRef={refreshRef}
                                calendarReady={!loading && !isTransitioning && events !== null}
                            />
                            <GradesBtn disabled={cacheOnly} onClick={() => setShowGradesModal(true)} />
                            <SettingsMenu
                                coursesDisabled={cacheOnly}
                                onOpenCourses={handleOpenCourseModal}
                                onOpenFeatures={() => setShowFeaturesModal(true)}
                            />
                            <ServiceHealthIndicator refreshKey={events?.cached_at} />
                        </div>
                        <div className="header-actions">
                            <ExitBtn onExit={handleLogoutRequest} />
                        </div>
                    </div>


                    <div className="events-container">
                        <ErrorBoundary resetKey={selectedEvent}>
                            <EventsDetail event={selectedEvent} mtsUrls={mtsUrls} onOpenGrades={handleOpenGradesFromCard} />
                        </ErrorBoundary>
                    </div>
                    <DatePicker setDate={setDate} initialDate={date} disableButtons={loading} />
                </header>

                {/* Mobile Controls Container */}
                <div className="mobile-controls-container">
                    <div className="mobile-header-actions">
                        <GradesBtn disabled={cacheOnly} onClick={() => setShowGradesModal(true)} />
                        <div className="mobile-buttons-row">
                            <CalendarExportMenu
                                disabled={cacheOnly}
                                date={date}
                                onOpenSubscription={() => setShowSubscriptionModal(true)}
                            />
                            <CacheUpdateBtn 
                                disabled={cacheOnly}
                                date={date} 
                                onDataUpdate={handleDataUpdate}
                                cachedAt={events?.cached_at}
                                failures={events?.failures}
                                refreshRef={refreshRef}
                                calendarReady={!loading && !isTransitioning && events !== null}
                            />
                        </div>
                        <div className="mobile-buttons-row">
                            <SettingsMenu
                                coursesDisabled={cacheOnly}
                                onOpenCourses={handleOpenCourseModal}
                                onOpenFeatures={() => setShowFeaturesModal(true)}
                            />
                            <button
                                className="features-trigger-btn mobile-features-btn"
                                onClick={handleLogoutRequest}
                                title="Выйти из системы"
                            >
                                🚪 Выйти
                            </button>
                        </div>
                    </div>
                    <SimpleDatePicker setDate={setDate} initialDate={date} disableButtons={loading} />
                </div>

                {cacheOnly && (
                    <div className="service-status-banner service-status-banner--cached" role="status">
                        <div className="service-status-body">
                            <div className="service-status-title">Просмотр сохраненного расписания</div>
                            <div className="service-status-hint">
                                {events?.cached_at && `Копия от ${new Date(events.cached_at).toLocaleString('ru-RU')}. `}
                                Данные могут быть неактуальны. Доступны только ранее загруженные недели.
                                Обновление, оценки и новые недели будут доступны после входа.
                            </div>
                        </div>
                        <button type="button" className="service-status-retry" onClick={() => navigate('/login')}>
                            Войти заново
                        </button>
                    </div>
                )}
                {!cacheOnly && restarting && <ServiceRestartNotice checking={loading} />}
                {!cacheOnly && !loading && (
                    <ServiceStatusBanner
                        failures={events?.failures}
                        onRetry={handleRetryServices}
                        retrying={retryingServices}
                    />
                )}

                <div className={`calendar ${loading || isTransitioning ? 'calendar-loading' : 'calendar-loaded'}`}>
                    {loading ? (
                        <Loader />
                    ) : cacheMessage ? (
                        <div className="cached-week-empty" role="status">{cacheMessage}</div>
                    ) : (
                        <>
                            {/* Desktop Calendar Table */}
                            <table className="shedule-table">
                                <thead>
                                    <DaysNumber date={date} />
                                    <DeadLine date={date} events={events} setSelectedEvent={setSelectedEvent} />
                                </thead>
                                <tbody>
                                    <LessonTimes
                                        events={events}
                                        selectedEvent={selectedEvent}
                                        setSelectedEvent={setSelectedEvent}
                                    />
                                </tbody>
                            </table>
                            
                            {/* Mobile Calendar View */}
                            <MobileCalendarView
                                events={events}
                                selectedEvent={selectedEvent}
                                setSelectedEvent={setSelectedEvent}
                                date={date}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CalendarPage;
